import * as React from 'react';
import { cn } from '../../lib/cn';
import { textToneStyles } from '../../lib/tone';
import type { TextAlign, TextTone, TextWeight } from '../../lib/types';

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

/** Independent of the semantic level. */
type HeadingVariant = 'display' | 'heading' | 'subheading' | 'eyebrow';

export interface HeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
  /**
   * Picks the element (h1-h6).
   * @default 2
   */
  level?: HeadingLevel;
  /** @default 'heading' */
  variant?: HeadingVariant;
  weight?: TextWeight;
  align?: TextAlign;
  /**
   * Unlike `Text`, `default` sets the foreground colour instead of inheriting
   * the parent's.
   * @default 'default'
   */
  tone?: TextTone;
  className?: string;
  ref?: React.Ref<HTMLHeadingElement>;
  children?: React.ReactNode;
}

const variantStyles = {
  display: 'text-4xl md:text-5xl font-bold tracking-tight',
  heading: 'text-2xl md:text-3xl font-semibold tracking-tight',
  subheading: 'text-lg md:text-xl font-medium',
  // Deliberately one size at every breakpoint, unlike the others: a caller
  // shrinking one of those `md:` sizes has to override both anyway.
  eyebrow: 'text-xs font-semibold tracking-wide uppercase',
} satisfies Record<HeadingVariant, string>;

const toneStyles = {
  ...textToneStyles,
  default: 'text-foreground',
} satisfies Record<TextTone, string>;

const alignStyles = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
} satisfies Record<TextAlign, string>;

const weightStyles = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
} satisfies Record<TextWeight, string>;

export function Heading({
  level = 2,
  variant = 'heading',
  weight,
  align,
  tone = 'default',
  className,
  ref,
  ...props
}: HeadingProps) {
  const Component = `h${level}` as const;

  return (
    <Component
      ref={ref}
      className={cn(
        toneStyles[tone],
        variantStyles[variant],
        weight && weightStyles[weight],
        align && alignStyles[align],
        className,
      )}
      {...props}
    />
  );
}
